import store from 'store'
import {
  RECEIVED_USER,
  RECEIVED_USERS,
  REQUESTED_USER,
  REQUESTED_USERS,
  REQUESTED_USERS_FAILED,
  REQUESTED_USERS_SUCCEEDED,
  REQUESTED_USER_FAILED,
  REQUESTED_USER_SUCCEEDED,
  RESET_USER,
  MFA_REQUIRED,
  MFA_VERIFY,
  MFA_VERIFY_SUCCESS,
  MFA_VERIFY_FAILED,
  MFA_RESET,
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_SUCCESS,
  FORGOT_PASSWORD_FAILED,
  FORGOT_PASSWORD_RESET,
  CHANGE_PASSWORD,
  CHANGE_PASSWORD_SUCCESS,
  CHANGE_PASSWORD_FAILED,
  CHANGE_PASSWORD_RESET,
  UserActionImpl,
  UserImpl,
  UsersImpl,
  UserPartialImpl,
  MfaState,
  MfaRequiredPayload,
  ForgotPasswordState,
  ChangePasswordState,
} from './types'

const initialState: UserImpl = {
  id: '',
  name: '',
  isVerified: false,
  role: null,
  email: '',
  avatar: '',
  authorized: !!store.get('token') || !!store.get('user'),
  isLoading: false,
  hasError: false,
  permissions: null,
  requestedPath: '',
  firstName: '',
  lastName: '',
  phone: '',
  username: '',
  displayName: '',
  contentPartnerId: null,
  contentPartnerName: ''
}

export function user(state = initialState, action: UserActionImpl): UserImpl {
  switch (action.type) {
    case REQUESTED_USER:
      return { ...state, isLoading: true, hasError: false }
    case REQUESTED_USER_SUCCEEDED:
      return { ...state, isLoading: false, hasError: false }
    case REQUESTED_USER_FAILED:
      return { ...state, isLoading: false, hasError: true }
    case RECEIVED_USER: {
      return { ...state, ...(<UserImpl>action.payload) }
    }
    case RESET_USER:
      return { ...initialState, authorized: false }
    default:
      return state
  }
}

// MFA reducer
const initialMfaState: MfaState = {
  mfaRequired: false,
  mfaId: null,
  mfaMethod: null,
  mfaSetupRequired: false,
  setupUri: null,
  passwordChangeRequired: false,
  isVerifying: false,
  verifyError: null,
}

export function mfa(state = initialMfaState, action: UserActionImpl): MfaState {
  switch (action.type) {
    case MFA_REQUIRED: {
      const payload = action.payload as unknown as MfaRequiredPayload
      return {
        ...state,
        mfaRequired: true,
        mfaId: payload.mfaId,
        mfaMethod: payload.mfaMethod,
        mfaSetupRequired: payload.mfaSetupRequired,
        setupUri: payload.setupUri,
        passwordChangeRequired: payload.passwordChangeRequired,
        verifyError: null,
      }
    }
    case MFA_VERIFY:
      return { ...state, isVerifying: true, verifyError: null }
    case MFA_VERIFY_SUCCESS:
      return { ...initialMfaState }
    case MFA_VERIFY_FAILED:
      return { ...state, isVerifying: false, verifyError: action.payload as unknown as string }
    case MFA_RESET:
      return { ...initialMfaState }
    case RESET_USER:
      return { ...initialMfaState }
    default:
      return state
  }
}

// Forgot password reducer
const initialForgotPasswordState: ForgotPasswordState = {
  isLoading: false,
  isSuccess: false,
  error: null,
}

export function forgotPassword(
  state = initialForgotPasswordState,
  action: UserActionImpl,
): ForgotPasswordState {
  switch (action.type) {
    case FORGOT_PASSWORD:
      return { ...state, isLoading: true, error: null, isSuccess: false }
    case FORGOT_PASSWORD_SUCCESS:
      return { ...state, isLoading: false, isSuccess: true, error: null }
    case FORGOT_PASSWORD_FAILED:
      return { ...state, isLoading: false, isSuccess: false, error: action.payload as unknown as string }
    case FORGOT_PASSWORD_RESET:
      return { ...initialForgotPasswordState }
    default:
      return state
  }
}

// Change password reducer
const initialChangePasswordState: ChangePasswordState = {
  isLoading: false,
  isSuccess: false,
  error: null,
}

export function changePassword(
  state = initialChangePasswordState,
  action: UserActionImpl,
): ChangePasswordState {
  switch (action.type) {
    case CHANGE_PASSWORD:
      return { ...state, isLoading: true, error: null, isSuccess: false }
    case CHANGE_PASSWORD_SUCCESS:
      return { ...state, isLoading: false, isSuccess: true, error: null }
    case CHANGE_PASSWORD_FAILED:
      return { ...state, isLoading: false, isSuccess: false, error: action.payload as unknown as string }
    case CHANGE_PASSWORD_RESET:
      return { ...initialChangePasswordState }
    default:
      return state
  }
}

export const initialUsersState: { data: UsersImpl; isLoading: Boolean } = {
  data: {
    entities: {},
    allIds: [],
    adminIds: [],
    reviewerIds: [],
    specialistIds: [],
    users: [],
  },
  isLoading: false,
}

export function userList(state = initialUsersState, action: UserActionImpl) {
  switch (action.type) {
    case REQUESTED_USERS:
      return { ...state, isLoading: true, hasError: false }
    case REQUESTED_USERS_SUCCEEDED:
      return { ...state, isLoading: false, hasError: false }
    case REQUESTED_USERS_FAILED:
      return { ...state, isLoading: false, hasError: true }
    case RECEIVED_USERS: {
      return { ...state, data: <UsersImpl>action.payload }
    }
    default:
      return state
  }
}

// selectors

/**
 * Checks if user is admin
 * @param {object} state user state object
 * @returns {boolean}
 */
export const amIAdmin = (state: UserImpl): Boolean => state.role === 'admin'

/**
 * Checks if user is academic head
 * @param {object} state user state object
 * @returns {boolean}
 */
export const amIAcademicHead = (state: UserImpl): Boolean => state.role === 'academic-head'

/**
 * returns concerned user object
 * @param state user list state
 * @param id user id
 */
export const selectUser = (state: UsersImpl, id: string): UserPartialImpl => state.entities[id]

/**
 * Checks if user is verified user
 * @param {object} state user state object
 * @returns {boolean}
 */
export const isVerifiedUser = (state: UserImpl): Boolean => state.isVerified
